import './Dashboard.css';
import React, { useState, useEffect, useCallback } from 'react';
import { Moon, Sun, ChevronDown, ChevronUp, Building2, LogOut, Coins, Trash2, Menu, X } from 'lucide-react';
import { collection, query, where, onSnapshot, getDoc, doc, setDoc } from 'firebase/firestore';
import { database } from './firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import OperatorEkle from './OperatorEkle';
import Puantajlar from './Puantajlar';
import MusteriListesi from './MusteriListesi';
import MakineTanımlama from './MakineTanımlama';
import OnayBekleyenCari from './OnayBekleyenCari';
import FirmaBilgileriGuncelle from './FirmaBilgileriGuncelle';
import Raporlama from './Raporlama';
import KontorAl from './KontorAl';
import CariKasa from './CariKasa';
import BankaTanitim from './BankaTanitim';
import AnaSayfa from './AnaSayfa';
import CopKutusu from './CopKutusu';
import { getFirestore } from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { Button } from '@mui/material';
import { redirectToLanding } from '../utils/landingRedirect';

const Dashboard = ({ userEmail, onSignOut }) => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');
  const [activeSection, setActiveSection] = useState('anaSayfa');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showFirmaBilgileri, setShowFirmaBilgileri] = useState(false);
  const [bekleyenMusteriSayisi, setBekleyenMusteriSayisi] = useState(0);
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    address: '',
    phone: ''
  });
  const [kontorAmount, setKontorAmount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Kurumsal kullanıcı kontrolü - Dashboard'a erişimi engelle
  useEffect(() => {
    const checkCorporateUser = async () => {
      if (!userEmail) return;
      
      try {
        const corporateUserRef = doc(database, 'corporateUsers', userEmail);
        const corporateUserDoc = await getDoc(corporateUserRef);
        
        if (corporateUserDoc.exists()) {
          // Kurumsal kullanıcı, kurumsal panele yönlendir
          console.log('⚠️ Kurumsal kullanıcı dashboard\'a erişmeye çalıştı, yönlendiriliyor...');
          navigate('/kurumsal/puantajlar');
        }
      } catch (error) {
        console.error('Kurumsal kullanıcı kontrolü hatası:', error);
      }
    };

    checkCorporateUser();
  }, [userEmail, navigate]);

  // Mobil algılama - window resize event listener
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Masaüstüne geçince menüyü kapat
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    // İlk yüklemede kontrol et
    checkMobile();

    // Resize event listener
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobil menü toggle fonksiyonu
  const toggleMobileMenu = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    }
  };

  // Menü item tıklandığında menüyü kapat (sadece mobilde)
  const handleMenuItemClick = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // Body scroll'u engelle menü açıkken
  useEffect(() => {
    if (mobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen, isMobile]);

  useEffect(() => {
    if (theme === 'light') {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    } else {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    }
  }, [theme]);

  useEffect(() => {
    const fetchData = async () => {
      const db = getFirestore();
      const userEmail = auth.currentUser?.email;
      
      if (!userEmail) {
        console.error('Kullanıcı email bilgisi bulunamadı');
        return;
      }

      try {
        // User-based path kullan
        const q = query(collection(db, `users/${userEmail}/customerList`), where('Onay', '==', 'Onay Bekliyor'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const bekleyenCount = snapshot.size;
          setBekleyenMusteriSayisi(bekleyenCount);
        }, (error) => {
          console.error('Firestore dinleme hatası:', error);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
      }
    };

    fetchData();
  }, []);

  // Real-time kontör bilgisini dinle
  useEffect(() => {
    if (!userEmail) return;
    
    const kontorDocRef = doc(database, `users/${userEmail}/kontor/current`);
    
    // Real-time dinleyici
    const unsubscribe = onSnapshot(kontorDocRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const kontorData = docSnapshot.data();
        const newAmount = kontorData.amount || 0;
        setKontorAmount(newAmount);
        console.log('Kontör güncellendi:', newAmount);
      } else {
        console.log('Kontor bilgisi bulunamadı, yeni belge oluşturuluyor');
        setKontorAmount(0);
        
        // Kontor belgesini oluştur
        try {
          await setDoc(kontorDocRef, {
            amount: 0,
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            history: [
              {
                date: new Date().toISOString(),
                amount: 0,
                type: 'initial',
                description: 'Başlangıç kontör kaydı'
              }
            ]
          });
        } catch (err) {
          console.error('Kontor belgesi oluşturulurken hata:', err);
        }
      }
    }, (error) => {
      console.error('Kontor dinleyici hatası:', error);
      setKontorAmount(0);
    });
    
    // Cleanup fonksiyonu
    return () => unsubscribe();
  }, [userEmail]);

  const fetchUserData = useCallback(async () => {
    const db = database;
    try {
      const userDoc = await getDoc(doc(db, 'users', userEmail));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({
          ...data,
          companyName: data.company || data.companyName || '',
          address: data.address || '',
          phoneNumber: data.phoneNumber || '',
          logoUrl: data.logoUrl || ''
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    // Firma bilgilerini getir
    const fetchCompanyInfo = async () => {
      if (!userEmail) return;

      const db = getFirestore();
      const userDocRef = doc(db, 'users', userEmail);
      
      try {
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCompanyInfo({
            name: data.companyName || '',
            address: data.address || '',
            phone: data.phone || ''
          });
        }
      } catch (error) {
        console.error('Firma bilgileri alınamadı:', error);
      }
    };

    fetchCompanyInfo();
  }, [userEmail]);

  // Auth state'ini kontrol et
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        // Kullanıcı oturumu kapanmışsa login sayfasına yönlendir
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFirmaBilgileriClick = () => {
    setShowFirmaBilgileri(true);
  };

  const handleFirmaBilgileriClose = () => {
    setShowFirmaBilgileri(false);
    fetchUserData();
  };

  const handleUserDataUpdate = (updatedData) => {
    console.log('🔄 Dashboard: User data güncelleniyor:', updatedData);
    setUserData(prevData => ({
      ...prevData,
      ...updatedData
    }));
  };

  const handleSignOutClick = () => {
    const isConfirmed = window.confirm('Çıkış yapmak istediğinize emin misiniz?');
    if (!isConfirmed) return;

    // signOut'u beklemeden hemen yönlendir; React rerender yarışını önler.
    signOut(auth).catch((err) => console.error('signOut hata:', err));
    if (typeof onSignOut === 'function') onSignOut();
    redirectToLanding();
  };

  const renderContent = () => {
    console.log('🎨 DASHBOARD: renderContent çağrıldı, activeSection:', activeSection);
    switch (activeSection) {
      case 'anaSayfa':
        return <AnaSayfa />;
      case 'puantajlar':
        return <Puantajlar userData={userData} />;
      case 'copKutusu':
        return <CopKutusu />;
      case 'raporlama':
        return <Raporlama theme={theme} />;
      case 'makineTanitma':
        return <MakineTanımlama />;
      case 'operatorEkle':
        return <OperatorEkle />;
      case 'musteriListesi':
        return <MusteriListesi />;
      case 'onayBekleyenCari':
        return <OnayBekleyenCari />;
      case 'kontorAl':
        return <KontorAl currentKontor={kontorAmount} onKontorUpdate={setKontorAmount} />;
      case 'bankaTanitma':
        console.log('🎯 DASHBOARD: Cari Kasa seçildi!');
        return <CariKasa />;
      case 'bankaTanitimKarti':
        console.log('🎯 DASHBOARD: Banka Tanıtım seçildi!');
        return <BankaTanitim />;
      default:
        return <AnaSayfa />;
    }
  };

  useEffect(() => {
    // Custom event listener for section changes from SetupGuide
    const handleSetActiveSection = (event) => {
      setActiveSection(event.detail);
    };

    // Custom event listener for opening FirmaBilgileri modal
    const handleOpenFirmaBilgileri = () => {
      setShowFirmaBilgileri(true);
    };

    window.addEventListener('setActiveSection', handleSetActiveSection);
    window.addEventListener('openFirmaBilgileri', handleOpenFirmaBilgileri);

    return () => {
      window.removeEventListener('setActiveSection', handleSetActiveSection);
      window.removeEventListener('openFirmaBilgileri', handleOpenFirmaBilgileri);
    };
  }, []);

  return (
    <div className="dashboard-layout">
      <div className="topbar">
        {/* Mobil Hamburger Menu Butonu */}
        <button className="mobile-menu-button" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        
        <div className="user-info" onClick={handleFirmaBilgileriClick} style={{ cursor: 'pointer' }}>
          {userData && userData.logoUrl ? (
            <img src={userData.logoUrl} alt="Company Logo" className="company-logo" />
          ) : (
            <Building2 size={24} />
          )}
          <span>{userData ? userData.companyName : userEmail}</span>
        </div>
        <div className="topbar-actions">
          <Button
            variant="contained"
            onClick={handleFirmaBilgileriClick}
            className="action-button firma-bilgileri-button"
            startIcon={<Building2 size={18} />}
          >
            Firma Bilgileri
          </Button>
          <Button
            variant="contained"
            className="action-button trash-button"
            startIcon={<Trash2 size={18} />}
            onClick={() => setActiveSection('copKutusu')}
          >
            Çöp Kutusu
          </Button>
          <Button
            variant="contained"
            className="action-button kontor-button"
            startIcon={<Coins size={18} />}
            onClick={() => setActiveSection('kontorAl')}
          >
            Kontör: {kontorAmount}
          </Button>
          <Button
            variant="contained"
            onClick={toggleTheme}
            className="action-button theme-toggle-button"
            startIcon={theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            title={theme === 'light' ? 'Karanlık Mod' : 'Aydınlık Mod'}
          >
            {theme === 'light' ? 'Karanlık' : 'Aydınlık'}
          </Button>
          <div style={{ marginLeft: 'auto' }}>
            <Button
              variant="contained"
              onClick={handleSignOutClick}
              className="action-button sign-out-button"
              startIcon={<LogOut size={18} />}
            >
              Çıkış
            </Button>
          </div>
        </div>
      </div>

      <div className="main-content">
        {/* Mobil Menu Overlay */}
        <div 
          className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        <div className={`leftbar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <h2 
            onClick={() => handleMenuItemClick('anaSayfa')}
            style={{ cursor: 'pointer' }}
          >
            Ana Sayfa
          </h2>
          <ul>
            <li>
              <button onClick={() => handleMenuItemClick('puantajlar')}>
                Puantajlar
              </button>
            </li>
            <li>
              <button onClick={() => handleMenuItemClick('raporlama')}>
                Raporlama
              </button>
            </li>
            <li>
              <button onClick={() => {
                console.log('🔗 MENU: Cari Kasa menüsüne tıklandı!');
                handleMenuItemClick('bankaTanitma');
              }}>
                Cari Kasa
              </button>
            </li>
            <li className="dropdown">
              <button onClick={toggleDropdown}>
                Tanıtım Kartları {isDropdownOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {isDropdownOpen && (
                <ul className="dropdown-menu">
                  <li>
                    <button onClick={() => handleMenuItemClick('makineTanitma')}>
                      Makine Tanıtım
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleMenuItemClick('operatorEkle')}>
                      Operatör Tanıtım
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleMenuItemClick('musteriListesi')}>
                      Cari Tanıtım
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleMenuItemClick('onayBekleyenCari')}>
                      Onay Bekleyen Cari Tanıtım
                      {bekleyenMusteriSayisi > 0 && (
                        <span className="notification-badge">{bekleyenMusteriSayisi}</span>
                      )}
                    </button>
                  </li>
                  <li>
                    <button onClick={() => handleMenuItemClick('bankaTanitimKarti')}>
                      Banka Tanıtım
                    </button>
                  </li>
                </ul>
              )}
            </li>
            <li>
              <button onClick={() => handleMenuItemClick('kontorAl')}>
                Kontör Al
              </button>
            </li>
          </ul>
          <div className="company-info-footer">
            <h1 className="hops-title">HOPS</h1>
            {companyInfo.name && (
              <div className="company-details">
                <p>{companyInfo.name}</p>
                <p>{companyInfo.address}</p>
                <p>{companyInfo.phone}</p>
              </div>
            )}
            <div className="contact-info">
              <h3>İletişim Bilgileri</h3>
              <p><strong>Telefon:</strong> +905524641116</p>
              <p><strong>E-posta:</strong> info@hopssoftware.com</p>
              <p><strong>Adres:</strong> Yeniköy Merkez Mah. Vatan Cad. Teknopark Sitesi Başiskele / Kocaeli</p>
            </div>
          </div>
        </div>

        <div className="content">
          {renderContent()}
        </div>
      </div>

      {showFirmaBilgileri && (
        <FirmaBilgileriGuncelle
          onClose={handleFirmaBilgileriClose}
          onUpdate={handleUserDataUpdate}
          userData={userData}
          userEmail={userEmail}
        />
      )}
    </div>
  );
};

export default Dashboard;